import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Policy, PaginatedPolicyResponse } from '../../models/policy.interface';
import { UrlReferenceService } from './url-reference.service';
import { PolicyData, PolicyResponse } from '../../models/PolicyResponse';

export enum PolicyStatus {
  ACTIVE = 'ACTIVE',
  RENEW = 'RENEW', 
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
  PENDING = 'PENDING'
}

export interface PolicySearchParams {
  searchTerm?: string; // Unified search term for company name, payer name, or policy number
  companyName?: string; // Keep for backward compatibility
  payerName?: string; // Keep for backward compatibility
  policyNumber?: string; // Keep for backward compatibility
  status?: PolicyStatus;
  page?: number;
  size?: number;
  sort?: string;
}

export interface IssuePolicyRequest {
  companyId: number;
  quotaReferenceNo: string;
}

@Injectable({
  providedIn: 'root',
})
export class PolicyService {
  constructor(private http: HttpClient, private urlRef: UrlReferenceService) {}

  getAllPolicies<
    PaginatedPolicyResponse
  >(): Observable<PaginatedPolicyResponse> {
    return this.http.get<PaginatedPolicyResponse>(
      `${this.urlRef.policiesBaseUrl}/active`
    );
  }

  getActivePoliciesByUserId(userId: string, companyId: number): Observable<PaginatedPolicyResponse> {
    const params = new HttpParams().set('userId', userId).set('companyId', companyId);

    return this.http.get<PaginatedPolicyResponse>(
      this.urlRef.activePoliciesUrl,
      { params }
    );
  }

  getRenewedPolicies(userId: string, companyId: number): Observable<PaginatedPolicyResponse> {
    const params = new HttpParams().set('userId', userId).set('companyId', companyId);
    return this.http.get<PaginatedPolicyResponse>(
      this.urlRef.renewedPoliciesUrl,
      { params }
    );
  }

  getExpiredPolicies(userId: string, companyId: number): Observable<PaginatedPolicyResponse> {
    const params = new HttpParams().set('userId', userId).set('companyId', companyId);
    return this.http.get<PaginatedPolicyResponse>(
      this.urlRef.expiredPoliciesUrl,
      { params }
    );
  }

  getCancelledPolicies(userId: string, companyId: number): Observable<PaginatedPolicyResponse> {
    const params = new HttpParams().set('userId', userId).set('companyId', companyId);
    return this.http.get<PaginatedPolicyResponse>(
      this.urlRef.cancelledPoliciesUrl,
      { params }
    );
  }

  getPendingPolicies(userId: string, companyId: number): Observable<PaginatedPolicyResponse> {
    const params = new HttpParams().set('userId', userId).set('companyId', companyId);
    return this.http.get<PaginatedPolicyResponse>(
      this.urlRef.pendingPoliciesUrl,
      { params }
    );
  }

  getPoliciesAboutToExpire(daysThreshold: number = 30): Observable<PaginatedPolicyResponse> {
    const params = new HttpParams().set('daysThreshold', daysThreshold.toString());
    
    return this.http.get<PaginatedPolicyResponse>(
      this.urlRef.aboutToExpirePoliciesUrl,
      { params }
    );
  }

  getPoliciesByStatus(status: string): Observable<Policy[]> {
    return this.http.get<Policy[]>(
      `${this.urlRef.currentBaseUrl}/policies/${status}`
    );
  }

  renewPolicy(policyId: string): Observable<Policy> {
    return this.http.post<Policy>(
      `${this.urlRef.currentBaseUrl}/policies/${policyId}/renew`,
      {}
    );
  }

  cancelPolicy(policyId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.urlRef.currentBaseUrl}/policies/${policyId}`
    );
  }

  downloadInvoice(policyNumber: string): Observable<Blob> {
    return this.http.get(
      `${this.urlRef.payersBaseUrl}/view-invoice/${policyNumber}`,
      { responseType: 'blob' }
    );
  }

  issuePolicy(request: IssuePolicyRequest): Observable<any> {
    return this.http.post(this.urlRef.createPolicyUrl(), request);
  }

  getPolicyDetailsById(policyId: string): Observable<PolicyResponse> {
    return this.http.get<PolicyResponse>(
      `${this.urlRef.policiesBaseUrl}/${policyId}/details`
    );
  }

  searchPolicies(searchParams: PolicySearchParams): Observable<PaginatedPolicyResponse> {
    let params = new HttpParams();

    // Handle unified search term
    if (searchParams.searchTerm) {
      params = params.set('searchTerm', searchParams.searchTerm);
    }
    
    // Keep individual field parameters for backward compatibility
    if (searchParams.companyName) {
      params = params.set('companyName', searchParams.companyName);
    }
    if (searchParams.payerName) {
      params = params.set('payerName', searchParams.payerName);
    }
    if (searchParams.policyNumber) {
      params = params.set('policyNumber', searchParams.policyNumber);
    }
    if (searchParams.status) {
      params = params.set('status', searchParams.status.toString());
    }
    if (searchParams.page !== undefined) {
      params = params.set('page', searchParams.page.toString());
    }
    if (searchParams.size !== undefined) {
      params = params.set('size', searchParams.size.toString());
    }
    if (searchParams.sort) {
      params = params.set('sort', searchParams.sort);
    }

    return this.http.get<PaginatedPolicyResponse>(
      this.urlRef.searchPoliciesUrl,
      { params }
    );
  }
}
